import { RefreshCw, ShieldAlert, ArrowLeft, Home } from "lucide-react";
import { useRouteError, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ErrorIllustration } from "@/components/icons/ErrorIllustration";
import { HeaderBar } from "@/components/layout/HeaderBar";

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
}

export const RouteErrorElement = () => {
  const error = useRouteError() as RouteError;
  const navigate = useNavigate();

  const isForbidden = error?.status === 403;

  return (
    <div className="min-h-screen flex flex-col bg-surface-0">
      <HeaderBar showLogo />

      <main className="flex-1 flex flex-col items-center justify-center p-6 text-center -mt-16">
        <div className="max-w-md w-full space-y-6">
          <div className="flex justify-center">
            {isForbidden ? (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
                <ShieldAlert className="h-12 w-12 text-red-600" />
              </div>
            ) : (
              <ErrorIllustration className="w-36 h-36" />
            )}
          </div>

          <div className="space-y-2">
            <h1 className="text-[20px] font-bold text-neutral-950">
              {isForbidden ? "Access Denied" : "Oops! Something went wrong."}
            </h1>
            <p className="text-sm text-neutral-600">
              {isForbidden
                ? "You don't have permission to access this page. Please contact your administrator if you believe you should have access."
                : "An unexpected error occurred. We're aware and working on it."}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {isForbidden ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="rounded-lg gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate("/")}
                  className="rounded-lg gap-2"
                >
                  <Home className="w-4 h-4" />
                  Return Home
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="rounded-lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh this page
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RouteErrorElement;
